import React, { useState } from "react";
import { Doughnut } from "react-chartjs-2";

import { Chart } from "chart.js";

import { useTranslation } from "utils";

import { GOAL_LEVEL_2 } from "models";

import { Flex, Tooltip } from "components";

import { CMC_ROUTES } from "pages/app/crop-mix-composer/CropMixComposerOverlay";

export enum ScoreColor {
  PINK = "#FF85C0",
  LIGHT_BLUE = "#00D4FF",
  PURPLE = "#8C75A9",
  BROWN = "#8E7973",
}

type ScoreCircleChartProps = {
  score: number;
  color: ScoreColor;
  size?: number;
  fontSize?: string;
  isPanel?: boolean;
  goal?: GOAL_LEVEL_2;
  dataTestId?: string;
  disableAnimation?: boolean;
};

const ScoreCircleChartComponent = ({ score, color, size, goal, isPanel = false, dataTestId = "", disableAnimation = false }: ScoreCircleChartProps) => {
  const { t } = useTranslation("cropMixComposer");
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const data = {
    datasets: [
      {
        data: [score, 100 - score],
        backgroundColor: [color, "#f0f0f0"],
        borderWidth: 0,
        cutout: isPanel ? "85%" : "70%",
      },
    ],
  };

  const doughnutLabel = {
    id: "doughnutLabel",
    beforeDatasetsDraw(chart: Chart<"doughnut", number[], unknown>) {
      const { ctx, data } = chart;
      const xCoor = chart.getDatasetMeta(0).data[0].x;
      const yCoor = chart.getDatasetMeta(0).data[0].y;
      ctx.save();

      ctx.font = "bold 18px sans-serif";
      ctx.fillStyle = "black";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      ctx.fillText(`${data.datasets[0].data[0]}%`, xCoor, yCoor);
      ctx.restore();
    },
  };

  const options = {
    // Chart.js paints an animated chart over the frames that follow, which a print pass never sees.
    ...(disableAnimation ? { animation: false as const } : {}),
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 1,
    devicePixelRatio: 4,
    plugins: {
      tooltip: {
        enabled: false,
      },
      legend: {
        display: false,
      },
      datalabels: {
        display: false,
      },
    },
  };

  return (
    <Flex
      className="score-circle-chart-container"
      justify="center"
      style={{
        position: "relative",
        width: size ?? "100%",
      }}
    >
      {isPanel ? (
        <Tooltip
          style={{ display: "block", width: "100%" }}
          title={t(`page.${CMC_ROUTES.COMPOSITION}.panel.doughnutChartTooltip.${goal}`)}
          open={tooltipOpen}
          onOpenChange={setTooltipOpen}
          trigger="click"
          placement="top"
        >
          <Doughnut className="score-circle-chart" data={data} options={options} onMouseLeave={() => setTooltipOpen(false)} plugins={[doughnutLabel]} />
        </Tooltip>
      ) : (
        <>
          <Doughnut className="score-circle-chart" data={data} options={options} />
          <Flex className="score-circle-chart-content" vertical justify="center" align="center" gap="0.5rem">
            <span data-testid={dataTestId} className={`goal-score paragraph-${isPanel ? "l-bold" : "xs"} margin-0`}>
              {`${Math.round(score)}${isPanel ? "%" : ""}`}
            </span>
          </Flex>
        </>
      )}
    </Flex>
  );
};

// every prop is primitive, so memoising stops one row change from redrawing every canvas in the table
export const ScoreCircleChart = React.memo(ScoreCircleChartComponent);
